import React, { useState, useCallback, useRef, useEffect, useMemo, createRef, } from "../../__compat__/react/main.entry.mjs.mjs";
import { useSpacing } from "../../api/spacing/useSpacing.mjs.mjs";
import { Badge } from "../Badge/index.mjs.mjs";
import { pickAriaProps, pickDataProps } from "../../interfaces/commonProps.mjs.mjs";
import { StyledAccordion, StyledSectionHeader, StyledSectionPanel, StyledSectionHeaderButton, StyledSectionHeaderLabel, StyledSectionPanelContent, StyledIcon, StyledSectionHeaderButtonContent, StyledIconContainer, StyledSectionHeaderHint, StyledSectionHeaderHintLabel, StyledSublineContainer, } from "./Accordion.styles.mjs.mjs";
const noop = () => { };
function setPanelHeight(node, value, defer = false) {
    const action = () => node.style.setProperty('--panel-height', value);
    if (defer) {
        window.requestAnimationFrame(action);
    }
    else {
        action();
    }
}
export const Accordion = React.forwardRef(function Accordion({ 'data-testid': testId, children, className, id, multiple, role, ...restProps }, ref) {
    const accordionSections = useMemo(() => children
        ? React.Children.map(children, (child) => child)
        : [], [children]);
    // When `multiple` is set this function acts as a mask so that only the first true value is kept.
    // The `stateFn` argument allows this to be applied to both `defaultOpen` and `open`.
    const stateMask = useCallback((stateFn) => {
        const firstIndex = accordionSections.findIndex((child) => stateFn(child.props));
        return accordionSections.map((child, index) => stateFn(child.props) && (multiple ? true : index === firstIndex));
    }, [accordionSections, multiple]);
    const defaultState = useMemo(() => stateMask((props) => Boolean(props.defaultOpen)), [stateMask]);
    const [state, setState] = useState(defaultState);
    // The `Accordion` will become controlled if `open` is explicitly set (not undefined) on any `AccordionSection`.
    const controlledState = useMemo(() => accordionSections.some((child) => child.props.open !== undefined)
        ? stateMask((props) => Boolean(props.open))
        : null, [accordionSections, stateMask]);
    useEffect(() => {
        if (controlledState)
            setState(controlledState);
    }, [controlledState]);
    const sectionToggleRefs = accordionSections.map(() => createRef());
    const handleKeyDown = (refIndex) => (event) => {
        let nextFocusIndex = -1;
        switch (event.key) {
            case 'Home':
                nextFocusIndex = 0;
                break;
            case 'End':
                nextFocusIndex = sectionToggleRefs.length - 1;
                break;
            case 'ArrowUp':
                nextFocusIndex =
                    refIndex === 0 ? sectionToggleRefs.length - 1 : refIndex - 1;
                break;
            case 'ArrowDown':
                nextFocusIndex =
                    refIndex === sectionToggleRefs.length - 1 ? 0 : refIndex + 1;
                break;
        }
        if (nextFocusIndex !== -1) {
            event.preventDefault();
            const nextToggle = sectionToggleRefs[nextFocusIndex].current;
            if (nextToggle)
                nextToggle.focus();
        }
    };
    const handleToggle = (sectionIndex, callback = noop) => (willOpen) => {
        const nextState = multiple ? [...state] : state.map(() => false);
        nextState[sectionIndex] = willOpen;
        setState(nextState);
        callback(willOpen);
    };
    // This is used to keep track of the section index since all children are mapped over, but only `AccordionSection` children are modified.
    let sectionIndex = 0;
    return (React.createElement(StyledAccordion, { className: className, "data-testid": testId, id: id, outerSpacing: useSpacing(restProps), ref: ref, role: role, ...pickAriaProps(restProps), ...pickDataProps(restProps) }, accordionSections.map((child) => {
        const modified = React.cloneElement(child, {
            open: controlledState
                ? controlledState[sectionIndex]
                : state[sectionIndex],
            defaultOpen: defaultState[sectionIndex],
            onToggle: controlledState
                ? child.props.onToggle
                : handleToggle(sectionIndex, child.props.onToggle),
            onKeyDown: handleKeyDown(sectionIndex),
            toggleRef: sectionToggleRefs[sectionIndex],
        });
        sectionIndex++;
        return modified;
    })));
});
export const AccordionSection = ({ children, headingLevel, id, label, subline, defaultOpen = false, open = defaultOpen, hint, hintSeverity, roleRegion, icon, toggleRef, onToggle = noop, onKeyDown = noop, }) => {
    const panelId = `${id}__panel`;
    const panelRef = useRef(null);
    const contentRef = useRef(null);
    const handleToggle = useCallback(() => onToggle(!open), [open, onToggle]);
    useEffect(() => {
        if (contentRef.current && panelRef.current) {
            setPanelHeight(panelRef.current, `${contentRef.current.scrollHeight}px`);
            window.requestAnimationFrame(() => {
                if (panelRef.current && contentRef.current) {
                    setPanelHeight(panelRef.current, open ? `${contentRef.current.scrollHeight}px` : '0px', true);
                }
            });
        }
    }, [open]);
    const handlePanelTransitionEnd = useCallback((event) => {
        if (open && panelRef.current && panelRef.current === event.target) {
            setPanelHeight(panelRef.current, 'auto', true);
        }
    }, [open]);
    const showHintBadge = hintSeverity && hint;
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledSectionHeader, { as: headingLevel },
            React.createElement(StyledSectionHeaderButton, { id: id, "aria-controls": panelId, "aria-expanded": open, onClick: handleToggle, onKeyDown: onKeyDown, ref: toggleRef },
                React.createElement(StyledSectionHeaderButtonContent, null,
                    icon && React.createElement(StyledIconContainer, null, icon),
                    React.createElement(StyledSectionHeaderLabel, null, label),
                    React.createElement(StyledSublineContainer, null, subline),
                    React.createElement(StyledSectionHeaderHint, { id: `${id}__hint` },
                        showHintBadge && (React.createElement(Badge, { variant: hintSeverity, spaceInlineEnd: "s" })),
                        React.createElement(StyledSectionHeaderHintLabel, null, hint)),
                    React.createElement(StyledIcon, { "$open": open, width: "24", height: "24", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg" },
                        React.createElement("path", { d: "M7 10L12.5 15.5 18 10", stroke: "currentColor", strokeWidth: "1", fill: "none", fillRule: "evenodd" }))))),
        React.createElement(StyledSectionPanel, { ref: panelRef, "$open": open, "$defaultOpen": defaultOpen, id: panelId, onTransitionEnd: handlePanelTransitionEnd, "aria-labelledby": roleRegion ? id : undefined, role: roleRegion ? 'region' : undefined },
            React.createElement(StyledSectionPanelContent, { ref: contentRef, "$open": open }, children))));
};
//# sourceMappingURL=Accordion.mjs.mjs.map